<template>
  <tr>
    <td colspan="5">
      <b>{{ index }}. {{ (inEnglish) ? article.name_en : article.name }}</b>
    </td>
  </tr>
  <template v-for="configuration in configurations" :key="configuration.id">
    <print-configuration
      :configuration="configuration"
      :article="article"
      :print-articles="printArticles"
      @remove="removeConfiguration"
      @show="show">
    </print-configuration>
  </template>
  <tr v-if="availablePrints.length > 0 && !isShopClosed">
    <td colspan="5">
      <span class="text-primary cursor--pointer" @click="showAddPrintModal">
        <i class="fa fa-plus mr-1"></i> {{ $t('configurator.buttons.add_print') }}
      </span>
    </td>
  </tr>
  <modal
    :is-active="addPrintModalActive"
    @close="hideAddPrintModal"
    :title="$t('configurator.modals.titles.add_print')">
    <template #body>
      <div class="form-horizontal">
        <div class="form-group">
          <label for="print" class="control-label">
            {{ $t('configurator.form.labels.choose_surface') }}
          </label>
          <div class="row">
            <div class="col-sm-12">
              <select class="form-control" name="print" v-model="printForm.print">
                <option v-for="availablePrint,index in availablePrints" :value="availablePrint" :key="index">
                  {{ !inEnglish ? availablePrint.name : availablePrint.name_en }}
                </option>
              </select>
              <p v-if="printForm.errors.has('print')"
                class="text-danger"
                v-html="$t('configurator.form.errors.graphic_design')">
              </p>
            </div>
          </div>
        </div>
          <div class="form-group" v-if="showCount">
            <label >{{ $t('configurator.form.labels.choose_size') }}:</label>
          <div class="row">
            <div class="col-sm-12">
              <input
                      class="form-control"
                      type="number"
                      :step="1"
                      :min="1"
                      :max="article.count"
                      :default="article.count"
                      name="depth"
                      v-model="printForm.count">
            </div>
          </div>
          </div>

        <div class="form-group">
          <label for="print_upload">{{ $t('configurator.form.labels.select_file') }}:</label>
          <drop-zone :id="dropzoneId" @drop.prevent="drop" @change="selectedFile"></drop-zone>
          <p class="help-block mb-1 pb-1" style="font-weight: 400;">
            {{ $t('configurator.form.labels.selected_file') }}: <span v-if="dropzoneFile">{{ dropzoneFile.name }}</span>
          </p>
          <printing-information></printing-information>
          <p v-if="printForm.errors.has('print_upload')"
            class="text-danger"
            v-html="$t('errors.print_upload')">
          </p>
        </div>
        <div class="form-group text-center">
          <button type="button" class="btn btn-warning mr-2" @click.prevent="hideAddPrintModal">
            {{ $t('configurator.buttons.cancel') }}
          </button>
          <button type="button" class="btn btn-primary" @click.prevent="addConfiguration">
            {{ $t('configurator.buttons.add') }}
          </button>
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup>
import { computed, reactive, ref } from 'vue'
import { useMainStore } from '@/store'
import { hideLoadingIndicator, showErrorToast, showLoadingIndicator, showWarningToast } from '@/utils/toast'
import Form from '@/classes/Form'
import FormDataForm from '@/classes/FormDataForm'
import { useI18n } from 'vue-i18n'

const { t } = useI18n({ useScope: 'global' })
const store = useMainStore()
// define props
const props = defineProps({
  index: {
    type: Number,
    required: true
  },
  article: {
    type: Object,
    required: true
  }
})
// define emits
const emit = defineEmits(['show'])
// define reactive props
const addPrintModalActive = ref(false)
const dropzoneFile = ref('')
const printForm = reactive(new Form({
  print: null,
  file: null,
  count: 1
}))
const uploadForm = reactive(new FormDataForm({
  file: null,
}))
// define computed props
const locale = computed(() => store.settings.locale)
const daysToGo = computed(() => store.tmp.days_to_go)
const shoppingCart = computed(() => store.settings.shopping_cart)
const exhibition = computed(() => store.tmp.exhibition_slug)
const isShopClosed = computed(() => store.tmp.is_exhibition_closed)

const inEnglish = computed(() => {
  return locale.value === 'en'
})

const printArticles = computed(() => {
  return shoppingCart.value.print_items.filter((item) => {
    return item.parent_id === props.article.id
  })
})

const hasPrints = computed(() => {
  return props.article.hasOwnProperty('prints')
})

const availablePrints = computed(() => {
  if (!hasPrints.value) {
    return []
  }

  if (configurations.value.length === 0) {
    return props.article.prints
  }

  const articleIds = configurations.value.map((configuration) => configuration.article_print_id)

  return props.article.prints.filter((item) => !articleIds.includes(item.id) )
})

const configurations = computed(() => {
  return shoppingCart.value.print_configuration
    .filter((configuration) => {
      return (
        configuration.parent_id === props.article.id
        && configuration.id === props.index
      )
    })
})

const showCount = computed(() => {
  if (printForm.print) {
    const printArticle = printArticles.value.find((item) => item.id === printForm.print.print_article_id)
    if (printArticle && printArticle.unit === 'sqm') {
      printForm.count = props.article.count
      return true
    }
  }
  printForm.count = 1
  return false
})

const dropzoneId = computed(() => {
  return 'dropzone_print_' + props.article.id + '_' + props.index
})
// define methods
const showAddPrintModal = () => {
  addPrintModalActive.value = true
}

const hideAddPrintModal = () => {
  addPrintModalActive.value = false
}

const drop = (e) => {
  dropzoneFile.value = e.dataTransfer.files[0]
}

const selectedFile = () => {
  dropzoneFile.value = document.getElementById(dropzoneId.value).files[0]
}

const upload = async () => {
  showLoadingIndicator(t('print_cloud.indicator.upload'))
  uploadForm.file = dropzoneFile.value
  await uploadForm.post('/' + exhibition.value + '/upload')
    .then(response => {
      uploadForm.resetToOriginal()
      printForm.file = response.data.file
      hideLoadingIndicator()
    })
    .catch(err => {
      if (err.status === 413) {
        showWarningToast(t('toast.warnings.too_large'), t('toast.title.warning'), 5000)
      } else {
        showErrorToast(err.message, t('toast.title.error'))
      }
    })
}

const addConfiguration = async () => {
  printForm.errors.clear()
  if (printForm.print === null) {
    printForm.errors.record({
      errors: {
        'print': t('configurator.form.errors.graphic_design')
      }
    })

    return
  }

  if (dropzoneFile.value !== '') {
    await upload()
  }

  // increase print items count for print article
  const printArticle = printArticles.value.find((item) => item.id === printForm.print.print_article_id)
  if (printArticle) {
    printArticle.count += printForm.count
    // add configuration
    shoppingCart.value.addPrintConfiguration(
      printForm.file,
      props.index,
      printForm.print.id,
      printArticle
    )
  } else {
    console.warn('print article not found')
  }
  printForm.resetToOriginal()
  dropzoneFile.value = ''
  hideAddPrintModal()
}

const removeConfiguration = (configuration) => {
  const itemIndex = printArticles.value.findIndex((item) => item.id === configuration.article_id)

  if (itemIndex > -1) {
    shoppingCart.value.removePrintConfiguration(
      configuration.id,
      configuration.article_print_id,
      printArticles.value[itemIndex]
    )


    const printArticle = printArticles.value.find((item) => item.id === configuration.article_id)
    printArticle.count -= printArticle.count
  }
}

const show = (article) => {
  emit('show', article)
}
</script>